import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useAmendmentDetailDispatch, useAmendmentDetailState } from './amendment-detail-context';
import AmendmentAppService from '../../../../../application/platform/sge/amendment/amendment-app-service';
import CommitmentAppService from '../../../../../application/platform/sge/commitment/commitment-app-service';
import LiquidationAppService from '../../../../../application/platform/sge/liquidation/liquidation-app-service';
import PaymentAppService from '../../../../../application/platform/sge/payment/payment-app-service';
import TransferAppService from '../../../../../application/platform/sge/transfer/transfer-app-service';
import AccountReportAppService from '../../../../../application/platform/sge/account-report/account-report-app-service';
import AttachmentAppService from '../../../../../application/platform/sge/attachment/attachment-app-service';
import ParlamentarianAppService from '../../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../../application/platform/sge/political-party/political-party-app-service';
import SupplierAppService from '../../../../../application/platform/sge/supplier/supplier-app-service';
import { useBaseLayout } from '../../../../../components/context/use-base-layout';
import { DefaultDialogProps } from '../../../../../components/default-dialog';

const toDateInputValue = (value?: string) => value ? value.substring(0, 10) : undefined;

export const useAmendmentDetailController = () => {
    const navigate = useNavigate();
    const dispatch = useAmendmentDetailDispatch();
    const state = useAmendmentDetailState();
    const { showMessage, showDialog } = useBaseLayout();
    const amendmentId = Number(state.amendmentId);
    const [isSaving, setIsSaving] = useState(false);

    function confirmDelete(message: string, onConfirm: () => void) {
        showDialog({
            open: true,
            title: 'Confirmar exclusão',
            message,
            type: 'question',
            onYesClick: onConfirm,
        } as DefaultDialogProps);
    }

    useEffect(() => {
        AmendmentAppService.getById(amendmentId).then((data) => {
            dispatch({ type: ACTION_TYPES.setAmendment, payload: data });
        });
        ParlamentarianAppService.list({ page: 0, pageSize: 100 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setParlamentarians, payload: r.items })
        );
        PoliticalPartyAppService.list({ page: 0, pageSize: 100 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setPoliticalParties, payload: r.items })
        );
        SupplierAppService.list({ page: 0, pageSize: 100 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setSuppliers, payload: r.items })
        );
        loadCommitments();
        loadTransfers();
        loadAccountReports();
        loadAttachments();
    }, []);

    function navigateToEdit() { navigate(`/emendas/editar/${amendmentId}`); }
    function navigateToList() { navigate('/emendas'); }

    useEffect(() => {
        if (state.selectedCommitment) {
            loadLiquidations(state.selectedCommitment.id);
        }
    }, [state.selectedCommitment?.id]);

    function loadCommitments() {
        CommitmentAppService.list(amendmentId, { page: 0, pageSize: 200 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setCommitments, payload: r.items })
        );
    }

    function loadLiquidations(commitmentId: number) {
        LiquidationAppService.list(commitmentId, { page: 0, pageSize: 200 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setLiquidations, payload: r.items })
        );
    }

    function loadPayments(liquidationId: number) {
        PaymentAppService.list(liquidationId, { page: 0, pageSize: 200 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setPayments, payload: r.items })
        );
    }

    function loadTransfers() {
        TransferAppService.list(amendmentId, { page: 0, pageSize: 200 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setTransfers, payload: r.items })
        );
    }

    function loadAccountReports() {
        AccountReportAppService.list(amendmentId, { page: 0, pageSize: 200 }).then((r) =>
            dispatch({ type: ACTION_TYPES.setAccountReports, payload: r.items })
        );
    }

    function loadAttachments() {
        AttachmentAppService.list('Amendment', amendmentId).then((data) =>
            dispatch({ type: ACTION_TYPES.setAttachments, payload: data })
        );
    }

    function handleTabChange(_: any, newValue: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex, payload: newValue });
    }

    function handleSelectCommitment(commitment: any) {
        dispatch({ type: ACTION_TYPES.setSelectedCommitment, payload: commitment });
        loadLiquidations(commitment.id);
    }

    // Commitment CRUD
    function openCommitmentDialog(existing?: any) {
        const form = existing ? { ...existing, commitmentDate: toDateInputValue(existing.commitmentDate), publicationDate: toDateInputValue(existing.publicationDate) } : {};
        dispatch({ type: ACTION_TYPES.setCommitmentForm, payload: form });
        dispatch({ type: ACTION_TYPES.setShowCommitmentValidationErrors, payload: false });
        dispatch({ type: ACTION_TYPES.setShowCommitmentDialog, payload: true });
    }

    function closeCommitmentDialog() {
        dispatch({ type: ACTION_TYPES.setShowCommitmentDialog, payload: false });
    }

    function handleCommitmentFormChange(e: any) {
        const { name, value } = e.target;
        const parsed = name === 'committedAmount' ? parseFloat(value) : name === 'supplierId' ? (value ? parseInt(value, 10) : undefined) : value;
        dispatch({ type: ACTION_TYPES.updateCommitmentForm, payload: { [name]: parsed } });
    }

    async function handleSaveCommitment() {
        const form = state.commitmentForm as any;
        if (!form.commitmentNumber || !form.commitmentDate || !form.publicationDate || !form.supplierId || !form.objectLink || !form.committedAmount) {
            dispatch({ type: ACTION_TYPES.setShowCommitmentValidationErrors, payload: true });
            return;
        }
        setIsSaving(true);
        try {
            if (form.id) {
                await CommitmentAppService.edit(amendmentId, form.id, form);
                showMessage({ open: true, message: 'Empenho atualizado com sucesso!', type: 'success' });
            } else {
                await CommitmentAppService.add(amendmentId, form);
                showMessage({ open: true, message: 'Empenho adicionado com sucesso!', type: 'success' });
            }
            closeCommitmentDialog();
            loadCommitments();
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar o empenho', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleDeleteCommitment(id: number) {
        confirmDelete('Tem certeza que deseja excluir este empenho?', async () => {
            try {
                await CommitmentAppService.remove(amendmentId, id);
                showMessage({ open: true, message: 'Empenho excluído com sucesso!', type: 'success' });
                loadCommitments();
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir o empenho', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    // Liquidation CRUD
    function openLiquidationDialog(existing?: any) {
        const form = existing ? { ...existing, liquidationDate: toDateInputValue(existing.liquidationDate) } : {};
        dispatch({ type: ACTION_TYPES.setLiquidationForm, payload: form });
        dispatch({ type: ACTION_TYPES.setShowLiquidationValidationErrors, payload: false });
        dispatch({ type: ACTION_TYPES.setShowLiquidationDialog, payload: true });
    }

    function closeLiquidationDialog() {
        dispatch({ type: ACTION_TYPES.setShowLiquidationDialog, payload: false });
    }

    function handleLiquidationFormChange(e: any) {
        const { name, value } = e.target;
        const parsed = name === 'liquidatedAmount' ? parseFloat(value) : value;
        dispatch({ type: ACTION_TYPES.updateLiquidationForm, payload: { [name]: parsed } });
    }

    async function handleSaveLiquidation() {
        if (!state.selectedCommitment) return;
        const form = state.liquidationForm as any;
        if (!form.liquidationNumber || !form.liquidationDate || !form.liquidatedAmount) {
            dispatch({ type: ACTION_TYPES.setShowLiquidationValidationErrors, payload: true });
            return;
        }
        setIsSaving(true);
        try {
            if (form.id) {
                await LiquidationAppService.edit(state.selectedCommitment.id, form.id, form);
                showMessage({ open: true, message: 'Liquidação atualizada com sucesso!', type: 'success' });
            } else {
                await LiquidationAppService.add(state.selectedCommitment.id, form);
                showMessage({ open: true, message: 'Liquidação adicionada com sucesso!', type: 'success' });
            }
            closeLiquidationDialog();
            loadLiquidations(state.selectedCommitment.id);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar a liquidação', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleDeleteLiquidation(id: number) {
        if (!state.selectedCommitment) return;
        const commitmentId = state.selectedCommitment.id;
        confirmDelete('Tem certeza que deseja excluir esta liquidação?', async () => {
            try {
                await LiquidationAppService.remove(commitmentId, id);
                showMessage({ open: true, message: 'Liquidação excluída com sucesso!', type: 'success' });
                loadLiquidations(commitmentId);
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir a liquidação', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    // Payment CRUD
    function openPaymentDialog(liquidationId: number, existing?: any) {
        const form = existing ? { ...existing, paymentDate: toDateInputValue(existing.paymentDate), _liquidationId: liquidationId } : { _liquidationId: liquidationId };
        dispatch({ type: ACTION_TYPES.setPaymentForm, payload: form });
        dispatch({ type: ACTION_TYPES.setShowPaymentDialog, payload: true });
    }

    function closePaymentDialog() {
        dispatch({ type: ACTION_TYPES.setShowPaymentDialog, payload: false });
    }

    function handlePaymentFormChange(e: any) {
        const { name, value } = e.target;
        const parsed = name === 'paidAmount' ? parseFloat(value) : value;
        dispatch({ type: ACTION_TYPES.updatePaymentForm, payload: { [name]: parsed } });
    }

    async function handleSavePayment() {
        const form = state.paymentForm as any;
        const { _liquidationId, id, ...payload } = form;
        setIsSaving(true);
        try {
            if (id) {
                await PaymentAppService.edit(_liquidationId, id, payload);
                showMessage({ open: true, message: 'Pagamento atualizado com sucesso!', type: 'success' });
            } else {
                await PaymentAppService.add(_liquidationId, payload);
                showMessage({ open: true, message: 'Pagamento adicionado com sucesso!', type: 'success' });
            }
            closePaymentDialog();
            loadPayments(_liquidationId);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar o pagamento', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    // Transfer CRUD
    function openTransferDialog(existing?: any) {
        const form = existing ? {
            ...existing,
            signatureDate: toDateInputValue(existing.signatureDate),
            effectiveStartDate: toDateInputValue(existing.effectiveStartDate),
            effectiveEndDate: toDateInputValue(existing.effectiveEndDate),
        } : {};
        dispatch({ type: ACTION_TYPES.setTransferForm, payload: form });
        dispatch({ type: ACTION_TYPES.setShowTransferValidationErrors, payload: false });
        dispatch({ type: ACTION_TYPES.setShowTransferDialog, payload: true });
    }

    function closeTransferDialog() {
        dispatch({ type: ACTION_TYPES.setShowTransferDialog, payload: false });
    }

    function handleTransferFormChange(e: any) {
        const { name, value } = e.target;
        const parsed = name === 'transferredAmount' ? parseFloat(value) : name === 'legalInstrumentType' ? (value ? parseInt(value, 10) : undefined) : value;
        dispatch({ type: ACTION_TYPES.updateTransferForm, payload: { [name]: parsed } });
    }

    async function handleSaveTransfer() {
        const form = state.transferForm as any;
        if (!form.companyName || !form.cnpj || !form.legalInstrumentType || !form.instrumentNumber || !form.signatureDate || !form.effectiveStartDate || !form.effectiveEndDate || !form.transferredAmount) {
            dispatch({ type: ACTION_TYPES.setShowTransferValidationErrors, payload: true });
            return;
        }
        setIsSaving(true);
        try {
            if (form.id) {
                await TransferAppService.edit(amendmentId, form.id, form);
                showMessage({ open: true, message: 'Repasse atualizado com sucesso!', type: 'success' });
            } else {
                await TransferAppService.add(amendmentId, form);
                showMessage({ open: true, message: 'Repasse adicionado com sucesso!', type: 'success' });
            }
            closeTransferDialog();
            loadTransfers();
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar o repasse', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleDeleteTransfer(id: number) {
        confirmDelete('Tem certeza que deseja excluir este repasse?', async () => {
            try {
                await TransferAppService.remove(amendmentId, id);
                showMessage({ open: true, message: 'Repasse excluído com sucesso!', type: 'success' });
                loadTransfers();
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir o repasse', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    // AccountReport CRUD
    function loadAccountReportAttachments(accountReportId: number) {
        AttachmentAppService.list('AmendmentAccountReport', accountReportId).then((data) =>
            dispatch({ type: ACTION_TYPES.setAccountReportAttachments, payload: data })
        );
    }

    function openAccountReportDialog(existing?: any) {
        const form = existing ? {
            ...existing,
            periodStartDate: toDateInputValue(existing.periodStartDate),
            periodEndDate: toDateInputValue(existing.periodEndDate),
            submissionDate: toDateInputValue(existing.submissionDate),
            reviewDate: toDateInputValue(existing.reviewDate),
        } : {};
        dispatch({ type: ACTION_TYPES.setAccountReportForm, payload: form });
        dispatch({ type: ACTION_TYPES.setAccountReportFiles, payload: [] });
        dispatch({ type: ACTION_TYPES.setAccountReportAttachments, payload: [] });
        dispatch({ type: ACTION_TYPES.setShowAccountReportDialog, payload: true });
        if (existing?.id) {
            loadAccountReportAttachments(existing.id);
        }
    }

    function closeAccountReportDialog() {
        dispatch({ type: ACTION_TYPES.setShowAccountReportDialog, payload: false });
    }

    function handleAccountReportFormChange(e: any) {
        const { name, value } = e.target;
        const parsed = name === 'status' ? (value ? parseInt(value, 10) : undefined) : value;
        dispatch({ type: ACTION_TYPES.updateAccountReportForm, payload: { [name]: parsed } });
    }

    function handleAccountReportFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
        const fileList = e.target.files;
        if (fileList) {
            dispatch({ type: ACTION_TYPES.setAccountReportFiles, payload: Array.from(fileList) });
        }
    }

    async function handleSaveAccountReport() {
        const form = state.accountReportForm as any;
        const payload = { ...form, files: state.accountReportFiles.length > 0 ? state.accountReportFiles : undefined };
        setIsSaving(true);
        try {
            if (form.id) {
                await AccountReportAppService.edit(amendmentId, form.id, payload);
                showMessage({ open: true, message: 'Prestação de contas atualizada com sucesso!', type: 'success' });
            } else {
                await AccountReportAppService.add(amendmentId, payload);
                showMessage({ open: true, message: 'Prestação de contas adicionada com sucesso!', type: 'success' });
            }
            closeAccountReportDialog();
            loadAccountReports();
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar a prestação de contas', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleDeleteAccountReport(id: number) {
        confirmDelete('Tem certeza que deseja excluir esta prestação de contas?', async () => {
            try {
                await AccountReportAppService.remove(amendmentId, id);
                showMessage({ open: true, message: 'Prestação de contas excluída com sucesso!', type: 'success' });
                loadAccountReports();
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir a prestação de contas', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    async function handleDownloadAccountReportAttachment(id: number, fileName: string) {
        try {
            const blob = await AttachmentAppService.download(id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível baixar o anexo', detail: error?.details || error?.message, type: 'error' });
        }
    }

    function handleDeleteAccountReportAttachment(id: number) {
        const reportId = (state.accountReportForm as any)?.id;
        confirmDelete('Tem certeza que deseja excluir este anexo?', async () => {
            try {
                await AttachmentAppService.remove(id);
                showMessage({ open: true, message: 'Anexo excluído com sucesso!', type: 'success' });
                if (reportId) {
                    loadAccountReportAttachments(reportId);
                }
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir o anexo', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    // Attachment
    async function handleUploadAttachment(file: File, title?: string) {
        try {
            await AttachmentAppService.upload({ entityType: 'Amendment', entityId: amendmentId, file, title });
            showMessage({ open: true, message: 'Anexo enviado com sucesso!', type: 'success' });
            loadAttachments();
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível enviar o anexo', detail: error?.details || error?.message, type: 'error' });
        }
    }

    async function handleDownloadAttachment(id: number, fileName: string) {
        try {
            const blob = await AttachmentAppService.download(id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível baixar o anexo', detail: error?.details || error?.message, type: 'error' });
        }
    }

    function handleDeleteAttachment(id: number) {
        confirmDelete('Tem certeza que deseja excluir este anexo?', async () => {
            try {
                await AttachmentAppService.remove(id);
                showMessage({ open: true, message: 'Anexo excluído com sucesso!', type: 'success' });
                loadAttachments();
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível excluir o anexo', detail: error?.details || error?.message, type: 'error' });
            }
        });
    }

    return {
        navigateToEdit, navigateToList, isSaving,
        handleTabChange, handleSelectCommitment, loadPayments,
        openCommitmentDialog, closeCommitmentDialog, handleCommitmentFormChange, handleSaveCommitment, handleDeleteCommitment,
        openLiquidationDialog, closeLiquidationDialog, handleLiquidationFormChange, handleSaveLiquidation, handleDeleteLiquidation,
        openPaymentDialog, closePaymentDialog, handlePaymentFormChange, handleSavePayment,
        openTransferDialog, closeTransferDialog, handleTransferFormChange, handleSaveTransfer, handleDeleteTransfer,
        openAccountReportDialog, closeAccountReportDialog, handleAccountReportFormChange, handleAccountReportFilesChange, handleSaveAccountReport, handleDeleteAccountReport,
        handleDownloadAccountReportAttachment, handleDeleteAccountReportAttachment,
        handleUploadAttachment, handleDownloadAttachment, handleDeleteAttachment,
    };
};
